import cx from 'classnames'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import UnreadBadge from 'components/UnreadBadge'
import { CurrentMemberContext } from 'contexts/CurrentMemberContext'
import { useBrandingSettings } from 'hooks/branding'
import { useTask } from 'hooks/mobx-hooks'
import link from 'infra/link'
import { observer } from 'mobx-react'
import NotificationsSidebar from 'notifications/views/NotificationsSidebar'
import React from 'react'
import { NavLink } from 'react-router-dom'
import { RootStore } from 'RootStore'
import styles from './Navigation.m.sass'
import ProfileDropdown from './ProfileDropdown'
import { IconButton } from 'components/IconButton'
import JobCreationDialogViewModel from 'jobs/creation/views/JobCreationDialog/JobCreationDialogViewModel'

interface Props {
  rootStore: RootStore
  floating: boolean
  children?: React.ReactNode
}

export default observer(function Navigation({
  children,
  rootStore,
  floating,
}: Props) {
  const { workspace } = rootStore.sessionStore
  const state = rootStore.authenticationViewModel

  const [profileDropdownVisible, setProfileDropdownVisible] =
    React.useState(false)

  const currentMember = React.useContext(CurrentMemberContext)
  const isFirmAdmin = rootStore.jobRootViewModel.isFirmAdmin
  const canViewFirmDashboard =
    rootStore.firmDashboardViewModel.canViewFirmDashboard

  const canViewTeams =
    rootStore.rootController.current?.teamsController.canViewTeams

  const canAccessUserManagement =
    rootStore.userManagementViewModel.canAccessUserManagement

  const canAccessClientUserManagement =
    rootStore.clientUserManagementViewModel.canAccessClientUserManagement

  const canDraftJob = rootStore.jobCreationRootVM.canDraft

  const { logoIconWhite } = useBrandingSettings()
  const canViewOps = useTask.pending(async () => {
    return (
      (await rootStore.opportunityStore.probeCanViewOpportunities()) ||
      (await rootStore.workspacePostingOpportunityStore?.canViewWorkspaceOpportunities())
    )
  }, [])
  const canViewProPayouts = useTask.pending(async () => {
    const vm = rootStore.proPayoutsViewModel
    if (!vm) {
      return false
    }
    await vm.checkCurrentUserProStatus()
    return vm.canView
  }, [])
  React.useEffect(() => {
    void canViewOps()
    void canViewProPayouts()
  }, [])

  const canViewScopingJobs =
    rootStore.ocrScopingJobsViewModel.ocrScopingJobsPermissionViewModel
      .canViewScopingJobs

  const pages = [
    {
      name: 'dashboard',
      path: link.firmDashboard(),
      disabled: !canViewFirmDashboard,
      exact: true,
      icon: <Icon name="dashboard" />,
      title: 'Dashboard',
    },
    {
      name: 'opportunities',
      path: link.opportunities(),
      disabled: !canViewOps.result,
      icon: <Icon name="opportunities" />,
      title: 'Opportunities',
      badgeCount: rootStore.opportunityStore.opportunityCount,
    },
    {
      name: 'jobs',
      path: link.jobs(),
      disabled: !workspace,
      icon: <Icon name="jobs" />,
      title: 'My Jobs',
      badgeCount: rootStore.conversationStore.totalUnreadCount,
    },
    {
      name: 'users',
      path: link.userManagement(),
      disabled: !canAccessUserManagement,
      icon: <Icon name="users" />,
      title: 'User Management',
    },
    {
      name: 'clients',
      path: link.clientUserManagement(),
      // Ensure that if you can both access clients and all users, that you only see 1 user
      // management link in the sidebar
      disabled: !canAccessClientUserManagement || canAccessUserManagement,
      icon: <Icon name="users" />,
      title: 'Client Management',
    },
    {
      name: 'teams',
      path: `${link.teams()}`,
      disabled: !workspace || !canViewTeams,
      icon: <Icon name="teams" />,
      title: 'Teams',
    },
    {
      name: 'documents-root',
      path: link.documents(),
      disabled: !currentMember.hasPermission('DOCS_VIEW'),
      icon: <Icon name="documents" />,
      title: 'Documents Center',
    },
    {
      name: 'pro-payouts',
      path: link.proPayouts(),
      disabled: !canViewProPayouts.result,
      icon: <Icon name="payout" />,
      title: 'Payouts',
    },
    {
      name: 'scoping-jobs',
      path: link.scopingJobs(),
      disabled: !canViewScopingJobs,
      icon: <Icon name="ocr" />,
      title: 'Scoping jobs',
    },
  ]

  return (
    <div
      className={cx(styles.container, floating && styles.floating)}
      data-test="RootNavigation"
    >
      <div className={styles.navigation}>
        <div
          className={styles.logo}
          title={`Worklayer Portal v${process.env.__VERSION__}`}
        >
          <a
            href={link.welcome()}
            className={styles.logoAnchor}
            data-test="Logo"
          >
            <img className={styles.logoImg} src={logoIconWhite} alt="Logo" />
          </a>
        </div>
        <div className={styles.separator} />
        <div className={styles.pages}>
          {pages
            .filter((page) => !page.disabled)
            .map((page) => (
              <Tooltip content={page.title} key={page.name}>
                <div className={styles.pageLink}>
                  <NavLink
                    className={styles.button}
                    to={page.path}
                    exact={page.exact}
                    activeClassName={styles.active}
                    data-test={page.name}
                  >
                    <span className={styles.icon}>{page.icon}</span>
                    {!!page.badgeCount && (
                      <div className={styles.unread}>
                        <UnreadBadge count={page.badgeCount} />
                      </div>
                    )}
                  </NavLink>
                </div>
              </Tooltip>
            ))}

          {canDraftJob && (
            <NewJobNavLink
              vm={rootStore.jobCreationDialogViewModel}
              name="create-new-job-button"
              title="New job"
              icon={<Icon name="add" />}
            />
          )}
        </div>
        <div className={styles.account}>
          <div className={styles.workspaces}>
            {state.workspaceSelectorState.memberships.length > 1 &&
              !isFirmAdmin && (
                <Tooltip
                  content={
                    state.workspaceSelectorState.isSelectorOpen
                      ? 'Close Workspace Switcher'
                      : 'Open Workspace Switcher'
                  }
                >
                  <button
                    onClick={() =>
                      state.workspaceSelectorState.toggleSelector()
                    }
                    data-test="WorkspaceSelectorToggle"
                    className={cx(
                      styles.workspacesButton,
                      state.workspaceSelectorState.isSelectorOpen &&
                        styles.active,
                    )}
                  >
                    <Icon name="switch-workspaces" />
                  </button>
                </Tooltip>
              )}
          </div>
          <div className={styles.notifications}>
            <Tooltip content="Notifications">
              <button
                className={styles.notificationsButton}
                data-test="NotificationsToggle"
                onClick={(e) => {
                  e.preventDefault()
                  return rootStore.notificationsSidebarVM.showing.toggle()
                }}
              >
                <Icon name="notifications" />
                {rootStore.notificationStore.unreadCount > 0 && (
                  <div className={styles.unread}>
                    <UnreadBadge
                      count={rootStore.notificationStore.unreadCount}
                    />
                  </div>
                )}
              </button>
            </Tooltip>
          </div>
          <div className={styles.profile}>
            <div className={styles.avatarContainer}>
              <a
                className={styles.profileButton}
                onClick={(e) => {
                  e.preventDefault()
                  return setProfileDropdownVisible(true)
                }}
                data-test="ProfileButton"
              >
                <Avatar size={30} subject={rootStore.sessionStore.member} />
              </a>
              <ProfileDropdown
                visible={profileDropdownVisible}
                onRequestClose={() => setProfileDropdownVisible(false)}
                sessionStore={rootStore.sessionStore}
                multiFactorAuthStore={rootStore.multiFactorAuthStore}
                workspaceSignupState={state.workspaceSignupState}
                isPayoutsEnabled={Boolean(rootStore.payoutsAdminViewModel)}
                adminVM={rootStore.adminViewModel}
                isFirmAdmin={isFirmAdmin}
                isPro={rootStore.jobRootViewModel.isPro}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.children}>{children}</div>
      <NotificationsSidebar vm={rootStore.notificationsSidebarVM} />
    </div>
  )
})

interface NewJobNavLinkProps {
  vm: JobCreationDialogViewModel
  name: string
  title: string
  icon: React.JSX.Element
}

function NewJobNavLink({ vm, name, title, icon }: NewJobNavLinkProps) {
  return (
    <Tooltip content={title}>
      <IconButton
        className={styles.button}
        onClick={() => vm.open()}
        dataTest={name}
      >
        <span className={styles.icon}>{icon}</span>
      </IconButton>
    </Tooltip>
  )
}
